import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Download, Loader2, FileWarning, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// File extensions we can preview natively in the dialog.
const PDF_EXT = ['pdf'];
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const TEXT_EXT = ['txt', 'csv', 'json', 'xml', 'md', 'log'];
// 3D formats supported by online-3d-viewer (STEP being our primary target).
const THREE_D_EXT = ['step', 'stp', 'stl', 'iges', 'igs', 'obj', '3mf', 'gltf', 'glb', 'fbx', '3ds', 'ply', 'brep', 'bim', 'fcstd'];

// Files larger than this skip 3D rendering (WASM STEP parsing gets slow > ~50MB).
const MAX_3D_PREVIEW_SIZE = 60 * 1024 * 1024;

function extOf(filename = '') {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

export function isPreviewable(file) {
  const ext = extOf(file?.original_filename);
  return (
    PDF_EXT.includes(ext) ||
    IMAGE_EXT.includes(ext) ||
    TEXT_EXT.includes(ext) ||
    THREE_D_EXT.includes(ext)
  );
}

function buildAuthedUrl(fileId) {
  const token = localStorage.getItem('pp_access_token') || '';
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}/api/files/download/${fileId}?auth=${encodeURIComponent(token)}`;
}

async function downloadAsBlob(fileId) {
  const res = await api.get(`/files/download/${fileId}`, { responseType: 'blob' });
  return res.data;
}

function PdfPreview({ file }) {
  const url = buildAuthedUrl(file.id);
  return (
    <iframe
      src={url}
      title={file.original_filename}
      className="w-full h-[70vh] bg-zinc-950 rounded-sm border border-zinc-800"
      data-testid="pdf-preview-iframe"
    />
  );
}

function ImagePreview({ file }) {
  const url = buildAuthedUrl(file.id);
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm flex items-center justify-center max-h-[70vh] overflow-auto">
      <img
        src={url}
        alt={file.original_filename}
        className="max-w-full max-h-[70vh] object-contain"
        data-testid="image-preview-img"
      />
    </div>
  );
}

function TextPreview({ file }) {
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const blob = await downloadAsBlob(file.id);
        const t = await blob.text();
        if (!cancelled) setText(t.slice(0, 200000)); // 200 KB cap
      } catch (e) {
        if (!cancelled) setErr('Impossible de charger le fichier');
      }
    })();
    return () => { cancelled = true; };
  }, [file.id]);
  if (err) return <FallbackPreview file={file} reason={err} />;
  return (
    <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-xs text-zinc-200 max-h-[70vh] overflow-auto whitespace-pre-wrap" data-testid="text-preview-pre">
      {text || 'Chargement...'}
    </pre>
  );
}

// 3D model viewer powered by online-3d-viewer (lazy-loaded so the lib is only
// downloaded when actually opening a 3D file).
// Note: STEP/IGES formats require the occt-import-js WASM, which the lib loads
// from https://cdn.jsdelivr.net/npm/occt-import-js/ at first use.
function ThreeDPreview({ file }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let viewer = null;
    let timeoutId = null;

    (async () => {
      if (file.size && file.size > MAX_3D_PREVIEW_SIZE) {
        setErr(`Fichier trop volumineux pour l'aperçu 3D (${(file.size / (1024 * 1024)).toFixed(1)} MB > 60 MB)`);
        setLoading(false);
        return;
      }
      try {
        const OV = await import('online-3d-viewer');
        if (cancelled || !containerRef.current) return;

        const params = {
          backgroundColor: new OV.RGBAColor(10, 10, 11, 255),
          defaultColor: new OV.RGBColor(180, 180, 180),
          edgeSettings: new OV.EdgeSettings(true, new OV.RGBColor(30, 30, 30), 1),
          onModelLoaded: () => {
            if (cancelled) return;
            if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
            setLoading(false);
          },
        };
        viewer = new OV.EmbeddedViewer(containerRef.current, params);

        // Download the file ourselves so we control the filename (online-3d-viewer
        // detects format from extension — our backend URL has no extension)
        const blob = await downloadAsBlob(file.id);
        if (cancelled) return;
        const f = new File([blob], file.original_filename || 'model.step', {
          type: file.content_type || 'application/octet-stream',
        });
        // Fire-and-watch: the lib calls onModelLoaded() on success, or shows an
        // inline error inside the canvas div on failure. We set a generous
        // timeout to swap to our Fallback UI if it never reports success
        // (e.g. CDN blocked, WASM failed to load, file corrupt).
        viewer.LoadModelFromFileList([f]);
        timeoutId = setTimeout(() => {
          if (cancelled) return;
          setErr('Le viewer 3D n\'a pas pu charger ce fichier (timeout). Téléchargez-le pour l\'ouvrir dans votre logiciel CAO.');
          setLoading(false);
        }, 45000);
      } catch (e) {
        console.error('3D preview error:', e);
        if (!cancelled) {
          setErr('Erreur de chargement de la librairie 3D');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      try { viewer && viewer.Destroy && viewer.Destroy(); } catch { /* ignore */ }
    };
  }, [file.id, file.original_filename, file.size, file.content_type]);

  if (err) {
    return <FallbackPreview file={file} reason={err} />;
  }
  return (
    <div className="relative bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="three-d-preview-wrap">
      <div ref={containerRef} style={{ width: '100%', height: '70vh' }} data-testid="three-d-canvas" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/80 backdrop-blur-sm pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-zinc-400 text-xs">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
            Chargement du modèle 3D…
            <span className="text-zinc-600 text-[10px]">
              {file.original_filename} ({((file.size || 0) / (1024 * 1024)).toFixed(1)} MB)
            </span>
            <span className="text-zinc-700 text-[10px]">
              (STEP/IGES : 1ère ouverture ~ 10-30s — téléchargement du moteur WASM)
            </span>
          </div>
        </div>
      )}
      {!loading && (
        <div className="absolute bottom-2 right-2 text-[10px] text-zinc-600 bg-zinc-950/80 px-2 py-1 rounded-sm border border-zinc-800 pointer-events-none">
          Clic-glisser : tourner · Molette : zoom · Maj+clic : déplacer
        </div>
      )}
    </div>
  );
}

function FallbackPreview({ file, reason }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm flex flex-col items-center justify-center h-[40vh] gap-3" data-testid="fallback-preview">
      <FileWarning className="w-10 h-10 text-amber-500/70" />
      <div className="text-sm text-zinc-300">{reason || 'Aperçu indisponible pour ce format'}</div>
      <div className="text-[11px] text-zinc-500">Vous pouvez le télécharger ci-dessous.</div>
      <div className="text-[10px] text-zinc-600 font-mono">{file.original_filename}</div>
    </div>
  );
}

export default function FilePreviewDialog({ file, fileId, open, onOpenChange }) {
  const [downloading, setDownloading] = useState(false);
  const [resolvedFile, setResolvedFile] = useState(file || null);
  const [metaLoading, setMetaLoading] = useState(false);

  // If only fileId is provided, fetch the metadata first.
  useEffect(() => {
    if (file) { setResolvedFile(file); return; }
    if (!fileId || !open) return;
    let cancelled = false;
    setMetaLoading(true);
    api.get(`/files/meta/${fileId}`)
      .then((r) => { if (!cancelled) setResolvedFile(r.data); })
      .catch(() => { if (!cancelled) toast.error('Fichier introuvable'); })
      .finally(() => { if (!cancelled) setMetaLoading(false); });
    return () => { cancelled = true; };
  }, [file, fileId, open]);

  const handleDownload = useCallback(async () => {
    if (!resolvedFile) return;
    setDownloading(true);
    try {
      const blob = await downloadAsBlob(resolvedFile.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resolvedFile.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur de téléchargement');
    } finally { setDownloading(false); }
  }, [resolvedFile]);

  if (!open) return null;
  if (metaLoading || !resolvedFile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="file-preview-dialog-loading">
          <div className="flex items-center justify-center py-10 gap-3 text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            Chargement du fichier…
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  const f = resolvedFile;
  const ext = extOf(f.original_filename);
  let body;
  if (PDF_EXT.includes(ext)) body = <PdfPreview file={f} />;
  else if (IMAGE_EXT.includes(ext)) body = <ImagePreview file={f} />;
  else if (TEXT_EXT.includes(ext)) body = <TextPreview file={f} />;
  else if (THREE_D_EXT.includes(ext)) body = <ThreeDPreview file={f} />;
  else body = <FallbackPreview file={f} />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 truncate">
            {f.original_filename}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {ext.toUpperCase()} · {f.size ? `${(f.size / 1024 / 1024).toFixed(2)} MB` : '—'}
            {f.uploaded_by_name ? ` · Ajouté par ${f.uploaded_by_name}` : ''}
          </DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="preview-close-btn">
            Fermer
          </Button>
          <Button asChild variant="outline" className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="preview-open-tab-btn">
            <a href={buildAuthedUrl(f.id)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Nouvel onglet
            </a>
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium"
            data-testid="preview-download-btn"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Télécharger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
